import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Wifi,
    Truck,
    Headphones,
    Briefcase,
    TrendingUp,
    BarChart3,
    Plug,
    Shield,
    LogOut,
    Users,
    MessageSquare,
    BookOpen,
} from "lucide-react";
import pt from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess } from "@/lib/rbac";
import { NxAvatar } from "@/components/ui/NxAvatar";

const ITEMS = [
    { key: "dashboard", to: "/dashboard", label: pt.nav.dashboard, icon: LayoutDashboard, section: null },
    { key: "services", to: "/servicos", label: pt.nav.services, icon: Wifi, section: "operations" },
    { key: "customers", to: "/clientes", label: pt.nav.customers, icon: Users, section: "operations" },
    { key: "tickets", to: "/tickets", label: pt.nav.cx, icon: Headphones, section: "operations" },
    { key: "conversations", to: "/conversas", label: pt.conversations.title, icon: MessageSquare, section: "operations" },
    { key: "kb", to: "/base-conhecimento", label: pt.kb.title, icon: BookOpen, section: "operations" },
    { key: "fieldOps", to: "/operacoes-campo", label: pt.nav.fieldOps, icon: Truck, section: "operations" },
    { key: "sales", to: "/vendas", label: pt.nav.sales, icon: Briefcase, section: "intelligence" },
    { key: "revenue", to: "/receita", label: pt.nav.revenue, icon: TrendingUp, section: "intelligence" },
    { key: "analytics", to: "/analitica", label: pt.nav.analytics, icon: BarChart3, section: "intelligence" },
    { key: "integrations", to: "/integracoes", label: pt.nav.integrations, icon: Plug, section: "platform" },
    { key: "admin", to: "/admin/utilizadores", label: pt.nav.admin, icon: Shield, section: "platform" },
];

const SECTION_LABEL = {
    operations: pt.nav.section_operations,
    intelligence: pt.nav.section_intelligence,
    platform: pt.nav.section_platform,
};

const Logomark = () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
        <defs>
            <linearGradient id="nx-grad" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#E8A96A" />
                <stop offset="1" stopColor="#A0612E" />
            </linearGradient>
        </defs>
        <path
            d="M6 24V8L18 20V8M6 8c-1 3-1.5 6-1.5 9M22 8c1 3 1.5 6 1.5 9M3 13c-1 3-1.5 6 0 9M27 13c1 3 1.5 6 0 9"
            stroke="url(#nx-grad)"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />
    </svg>
);

export const Sidebar = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const role = user?.role;

    const visibleItems = ITEMS.filter((i) => canAccess(i.key, role));
    const grouped = visibleItems.reduce((acc, i) => {
        const k = i.section || "_main";
        acc[k] = acc[k] || [];
        acc[k].push(i);
        return acc;
    }, {});

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <aside
            className="fixed inset-y-0 left-0 z-30 flex flex-col"
            style={{
                width: "var(--sidebar-width)",
                background: "var(--bg-sidebar)",
                backdropFilter: "blur(20px) saturate(1.4)",
                borderRight: "1px solid var(--glass-border)",
            }}
            data-testid="sidebar"
        >
            {/* Logo */}
            <div className="px-6 pt-6 pb-7">
                <div className="flex items-center gap-2.5">
                    <Logomark />
                    <div>
                        <div className="font-display text-xl text-[color:var(--slate-deep)] leading-none tracking-tight">
                            NexusTel
                        </div>
                        <div className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--copper-dark)] mt-1">
                            Angola Telecom
                        </div>
                    </div>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 px-4 overflow-y-auto pb-4" data-testid="sidebar-nav">
                {["_main", "operations", "intelligence", "platform"].map((sect) => {
                    const items = grouped[sect];
                    if (!items || !items.length) return null;
                    return (
                        <div key={sect} className="mb-5">
                            {SECTION_LABEL[sect] && (
                                <p className="px-4 mb-2 font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">
                                    {SECTION_LABEL[sect]}
                                </p>
                            )}
                            <ul className="space-y-0.5">
                                {items.map(({ to, label, icon: Icon, key }) => (
                                    <li key={key} className="px-2">
                                        <NavLink
                                            to={to}
                                            className={({ isActive }) =>
                                                "nav-item " + (isActive ? "active" : "")
                                            }
                                            data-testid={`nav-${key}`}
                                        >
                                            <Icon size={18} strokeWidth={1.6} />
                                            <span>{label}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            {/* User block */}
            <div
                className="m-4 p-3 rounded-xl flex items-center gap-3"
                style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--glass-border)",
                }}
                data-testid="sidebar-user"
            >
                <NxAvatar name={user?.full_name} size="md" />
                <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-[color:var(--slate-deep)] truncate font-medium">
                        {user?.full_name}
                    </p>
                    <p className="font-body text-[11px] text-[color:var(--slate-mid)] truncate">
                        {pt.users.roleLabels[user?.role] || user?.role}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="p-2 rounded-md text-[color:var(--slate-mid)] hover:text-[color:var(--error)] hover:bg-[rgba(192,57,43,0.08)] transition-colors"
                    aria-label={pt.nav.logout}
                    title={pt.nav.logout}
                    data-testid="logout-button"
                >
                    <LogOut size={16} strokeWidth={1.6} />
                </button>
            </div>
        </aside>
    );
};
